import { connect, useDispatch } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { Dispatch } from 'redux';
import { Form, FormRenderProps } from 'react-final-form';

import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { IKnmRemark } from '../../../APIPackage/CatalogsModule/interfaces/IKnmRemark';
import { getCatalogKnmRemarksAll } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import styles from './AdminEditForm.module.scss';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { adminCreateCatalogTypes, adminGetCatalogTypes, adminUpdateCatalogTypes } from '../actions/adminAction';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { ModalActions } from '../../../UIPackage/ModalModule/components/ModalActions';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { CheckboxField } from '../../../UIPackage/MainModule/components/CheckboxField';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { REQUIRED_ERROR } from '../../../CorePackage/MainModule/constants/validationErrors';

interface IKnmRemarkEditFormProps {
  onBack: () => void;
}

interface IKnmRemarkEditFormStoreProps {
  knmRemarks: IKnmRemark[];
}

type KnmRemarkForm = Partial<IKnmRemark>;

const KnmRemarkEditFormWithoutStore = ({
  onBack,
  knmRemarks,
}: IKnmRemarkEditFormProps & IKnmRemarkEditFormStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [selectedKnmRemark, setSelectedKnmRemark] = useState<KnmRemarkForm | undefined>();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isNew, setIsNew] = useState(false);

  useEffect(() => {
    dispatch(adminGetCatalogTypes(CommonCatalogsNamesEnum.KNM_REMARKS_ALL));
  }, []);

  const onCreate = useCallback(() => {
    setIsNew(true);
    setSelectedKnmRemark({
      enabled: false,
    });
    setIsFormOpen(true);
  }, []);

  const onEdit = useCallback(() => setIsFormOpen(true), []);

  const onCancel = useCallback(() => {
    setSelectedKnmRemark(undefined);
    setIsFormOpen(false);
  }, []);

  const onSubmit = useCallback(
    (values: KnmRemarkForm) => {
      if (isNew) {
        dispatch(adminCreateCatalogTypes({ data: values, catalogName: CommonCatalogsNamesEnum.KNM_REMARKS_ALL }));
      } else {
        dispatch(adminUpdateCatalogTypes({ data: values, catalogName: CommonCatalogsNamesEnum.KNM_REMARKS_ALL }));
      }

      onCancel();
    },
    [dispatch, isNew, onCancel],
  );

  const renderForm = ({ handleSubmit }: FormRenderProps<KnmRemarkForm>) => (
    <form onSubmit={handleSubmit}>
      <Row>
        <FieldBlock label="Название" color="black">
          <TextInputField name="name" validate={makeRequired(REQUIRED_ERROR)} />
        </FieldBlock>
      </Row>
      <Row>
        <FieldBlock label="Код раздела" color="black">
          <TextInputField name="sectionCode" validate={makeRequired(REQUIRED_ERROR)} disabled={!isNew} />
        </FieldBlock>
      </Row>
      <Row>
        <FieldBlock label="Вес" color="black">
          <TextInputField name="weight" type="number" min={1} validate={makeRequired(REQUIRED_ERROR)} />
        </FieldBlock>
      </Row>
      <Row>
        <CheckboxField name="enabled" label="Активен" id="enabled" type="checkbox" />
      </Row>
      <ModalActions>
        <Button color="primary" text="Сохранить" type="submit" variant="contained" />
        <Button color="default" text="Отмена" type="button" variant="contained" onClick={onCancel} />
      </ModalActions>
    </form>
  );

  if (isFormOpen) {
    return <Form<KnmRemarkForm> onSubmit={onSubmit} initialValues={selectedKnmRemark} render={renderForm} />;
  }

  return (
    <div>
      <div className={styles.Table}>
        <span className={styles.Header}>Замечания</span>
        {knmRemarks.map(item => (
          <button
            key={item.id}
            className={selectedKnmRemark?.id === item.id ? styles.DataRowSelected : styles.DataRow}
            data-id={item.id}
            type="button"
            onClick={() => setSelectedKnmRemark(item)}
          >
            {item.name}
          </button>
        ))}
      </div>
      <div className={styles.ControlButtons}>
        <Button
          className={styles.Button}
          color="default"
          type="button"
          variant="contained"
          onClick={onBack}
          text="Назад"
        />
        <Button
          className={styles.Button}
          color="primary"
          type="button"
          variant="contained"
          onClick={onCreate}
          text="Создать"
        />
        {selectedKnmRemark && (
          <Button
            className={styles.Button}
            color="default"
            type="button"
            variant="contained"
            onClick={onEdit}
            text="Редактировать"
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IKnmRemarkEditFormStoreProps => ({
  knmRemarks: getCatalogKnmRemarksAll(state),
});

export const KnmRemarkEditForm = connect(mapStateToProps)(KnmRemarkEditFormWithoutStore);
